import { useCallback, useContext } from 'react';

import { ADD_APP_PAGE, SETUP_APP_PAGE_BASE_PATH } from 'src/constants';
import { RouteContext } from 'src/context';
import { UrlUtils } from 'src/utils';

interface SetupAppPageUrlParams {
  moduleType: string;
  moduleId: string;
  preset?: string;
  appId?: string;
  /**
   * When set, records the page the user is currently on (`from` query param) so
   * the setup page can offer an accurate "Add an App" back crumb. Use this for
   * the add/install flow, not when editing an already-installed app.
   */
  fromCurrentPage?: boolean;
}

export const useAppSetupPaths = () => {
  const { pathname, query } = useContext(RouteContext);
  const { folderId } = query as { folderId?: string };

  const getAppDetailsPagePath = useCallback(
    (slug: string) => {
      const baseUrl = `/app-setup/${slug}`;
      return folderId ? `${baseUrl}?folderId=${folderId}` : baseUrl;
    },
    [folderId],
  );

  const getSetupAppPagePath = useCallback(
    ({
      moduleType,
      moduleId,
      preset,
      appId,
      fromCurrentPage,
    }: SetupAppPageUrlParams) => {
      const params = new URLSearchParams({ moduleType, moduleId });
      if (preset) {
        params.set('preset', preset);
      }
      if (appId) {
        params.set('appId', appId);
      }
      if (folderId) {
        params.set('folderId', folderId);
      }
      if (fromCurrentPage) {
        const queryString = UrlUtils.FormatUrlQueryToString(query);
        params.set(
          'from',
          queryString ? `${pathname}?${queryString}` : pathname,
        );
      }

      return `${SETUP_APP_PAGE_BASE_PATH}?${params.toString()}`;
    },
    [folderId, pathname, query],
  );

  return {
    /** folderId from the URL query param */
    folderId,
    /** path to the add app page */
    addAppPagePath: folderId
      ? `${ADD_APP_PAGE.path}?folderId=${folderId}`
      : ADD_APP_PAGE.path,
    /** builds the path to the app details page for a given slug */
    getAppDetailsPagePath,
    /** builds the full path to the setup app page */
    getSetupAppPagePath,
  } as const;
};
